import React from "react";

import "./Blog.css";
import NavLight from "../../components/NavLight";
import {Helmet} from "react-helmet";
import BlogsPreview from "../../components/BlogsPreview";
import FoundersGuide from "../../components/FoundersGuide";
import BlogPageIntroImg from "../../assets/blog/user_surveys_as_a_product_manager.png";
import {CustomMetaHelmet} from "../../components/CustomMetaHelmet";
import {meta} from "../../assets/meta/meta";

function HowToConductUserFeedbackSurvey() {
    return (
        <div className="Blog1">
            <CustomMetaHelmet meta={meta['how-to-conduct-user-surveys-as-a-product-manager']}/>
            <NavLight/>
            <section className="blog-page">
                <article className="blog-page-container">
                    <div>
                        <h1>How to conduct user surveys as a product manager</h1>
                        <p>
                            <i>
                                When product managers (PM’s) use user satisfaction surveys correctly, they can unlock incredible feedback that can help their product take off.
                                In this guide, I will walk you through some of the best practices when it comes to collecting user feedback through surveys.
                            </i>
                        </p>
                    </div>
                    <div>
                        <figure>
                            <img
                                src={BlogPageIntroImg}
                                alt="purple hand pressing a digital button"/>
                        </figure>
                    </div>
                    <div>
                        <h2>Identify your goals</h2>
                        <p>
                            Take a moment to reflect on what you want to learn from your users. Which areas of your
                            product would you like to explore more than others?
                            <p>&nbsp;</p>
                            For example, if you have just launched your minimum viable product, you might want to gain a
                            broad understanding of the users' overall experience.
                        </p>
                    </div>
                    <div>
                        <h2>Select your target users</h2>
                        <p>
                            Make sure your target users represent the user base you are targeting for your product,
                            whether it's your entire user base, a specific segment of users, or a random group.
                            <p>&nbsp;</p>
                            For example, we always recommend that PMs conduct beta testing.
                            Sending out surveys to your beta testing group will provide you with rich and timely feedback in a quantity that enables you to uncover patterns that can help you unlock growth.

                        </p>
                    </div>
                    <div>
                        <h2>Decide on a handful of good questions</h2>
                        <p>
                            While it's tempting to ask a lot of questions, I recommend keeping it to a maximum of 4-5
                            open-ended questions. Your questions should be tied to your goal and easily understood.
                            <p>&nbsp;</p>
                            For example, if you want to understand the overall experience, you might want to ask the user questions such as: What motivated them to try your product? What was the main benefit they got from your product? What would they like you to improve in order to better meet their needs?
                            As a general rule of thumb, always end the survey by asking the user if it's okay to follow up for clarification on one or more answers.
                            This will give you an opportunity to schedule user interviews to supplement with more rich feedback.
                        </p>
                    </div>
                     <div>
                        <h2>Use AI to ask good follow up questions</h2>
                        <p>
                            While traditional surveys with open ended questions can help a lot, unfortunately, they often lead to short and generic answers.
                            The challenges with short and generic answers is that it is incredibly difficult to derive any insights from them.
                            However, if you use Magic Surveys you can leverage the power of AI, by creating surveys that understand what users are answering in open-ended questions and automatically creates thoughtful follow up questions to unlock better survey answers and deeper insights.
                            <p>&nbsp;</p>
                            For example, you might include an open-ended question in your survey asking users what they think can be enhanced about your service.
                            A user might reply with a brief and generic answer like "better support."
                            Now, Magic Survey will automatically generate a follow-up inquiry based on the response to uncover deeper insights, such as, "Could you please elaborate a bit more on what you mean by 'better support'?"
                            As a result, the user goes into great depth describing how support failed to address their query and never followed up with the promised answers.
                            Just as a brilliant follow-up question can perform wonders in real life, it can be equally effective in surveys to acquire richer perspectives.
                        </p>
                    </div>
                    <div>
                        <h2>Send the survey</h2>
                        <p>
                            People are busy, and time is money.
                            Therefore, you need to sell your survey, not literally, but I recommend putting some effort into writing a personalized invitation message that persuades the user to fill out your survey.
                            Remember, the feedback from these surveys can have tremendous value for your product and its growth trajectory..
                            <p>&nbsp;</p>
                            For example, make sure you clearly explain why you are conducting the survey, how long it will take, and what users can gain from participating. Adapt the communication to the user's preferred style.
                            If someone prefers communication over LinkedIn, then send them a LinkedIn message.
                            Lastly, ensure that the message is short and concise, with a clear call-to-action and deadline for completing the survey.
                        </p>
                    </div>
                    <div>
                        <h2>Improve response rate</h2>
                        <p>
                            More often than not, people just need a little nudge through a follow-up to get things done.
                            However, remember to follow up respectfully and don't assume that people aren't answering the first time around because they don't want to.
                            Everyone has a busy life, and you should proactively follow up multiple times within a reasonable time before giving up.
                            <p>&nbsp;</p>
                            For example, I recommend following up respectfully three times.
                            The first time is simply a nudge, the second is when you're halfway through the deadline you set, and the third time's a few days before the deadline.
                            It's important to communicate respectfully and think long-term when building relationships.
                            Don't make the other person feel bad; instead, make them feel important. Their feedback is important to you!
                        </p>
                    </div>
                    <div>
                        <h2>Analyse the survey feedback</h2>
                        <p>
                            Once the deadline for submitting feedback through your surveys has passed, it's time to gather all the feedback and analyze it to identify patterns and insights.
                            Once you have organized everything in a prioritized order, it's time to discuss it with your tech lead.
                            <p>&nbsp;</p>
                            For example, you want to ensure that you organize and prioritize all the feedback from your open-ended questions.
                            With Magic Feedback, you can automatically organize and analyze all of your survey feedback without any hassle.
                            It provides you with all the patterns and insights organized in a prioritized order.
                            This allows you to collaborate better with your CTO and make data-driven decisions together.
                        </p>
                    </div>

                </article>
            </section>
            <section className="related-blogs">
                <h1> You might also like</h1>
                <BlogsPreview/>
            </section>
            <section className="FoundersGuide">
                <FoundersGuide/>
            </section>
        </div>
    );
}

export default HowToConductUserFeedbackSurvey;
